/** The whole control vocabulary: one toy material, sized for small hands. */

import type { ComponentChildren, JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { Minus, Plus } from 'lucide-preact';
import { CRAYONS, token } from '../engine/palette.ts';
import { play } from '../engine/audio.ts';

interface ToyProps {
  children?: ComponentChildren;
  onClick?: () => void;
  label: string;
  accent?: string;
  pressed?: boolean;
  round?: boolean;
  small?: boolean;
  wide?: boolean;
  ghost?: boolean;
  disabled?: boolean;
  hideLabel?: boolean;
}

function clickSound(): void {
  play('tap', 0.5);
}

export function ToyButton(props: ToyProps): JSX.Element {
  const classes = ['toy'];
  if (props.round) classes.push('toy--round');
  if (props.small) classes.push('toy--small');
  if (props.wide) classes.push('toy--wide');
  if (props.ghost) classes.push('toy--ghost');
  if (props.accent && !props.pressed) classes.push('toy--accent');
  return (
    <button
      type="button"
      class={classes.join(' ')}
      style={props.accent ? { '--accent': props.accent } : undefined}
      aria-pressed={props.pressed === undefined ? undefined : props.pressed}
      aria-label={props.hideLabel ? props.label : undefined}
      title={props.label}
      disabled={props.disabled}
      onClick={() => {
        clickSound();
        props.onClick?.();
      }}
    >
      {props.children}
      {!props.hideLabel && <span>{props.label}</span>}
    </button>
  );
}

interface ChipProps {
  label: string;
  onClick: () => void;
  pressed?: boolean;
  accent?: string;
  /** Carries the accent tint even when unselected — used to mark divisors. */
  tinted?: boolean;
  children?: ComponentChildren;
}

export function Chip(props: ChipProps): JSX.Element {
  return (
    <button
      type="button"
      class={props.tinted ? 'chip chip--tinted' : 'chip'}
      style={props.accent ? { '--accent': props.accent } : undefined}
      aria-pressed={props.pressed}
      onClick={() => {
        clickSound();
        props.onClick();
      }}
    >
      {props.children}
      <span>{props.label}</span>
    </button>
  );
}

interface MagicProps {
  label: string;
  accent?: string;
  onClick: () => void;
  children?: ComponentChildren;
  disabled?: boolean;
}

/** The one big call-to-action per screen. */
export function MagicButton(props: MagicProps): JSX.Element {
  return (
    <button
      type="button"
      class="magic"
      style={props.accent ? { '--accent': props.accent } : undefined}
      disabled={props.disabled}
      onClick={() => {
        play('sparkle', 0.5);
        props.onClick();
      }}
    >
      {props.children}
      <span>{props.label}</span>
    </button>
  );
}

interface CrayonRowProps {
  value: string;
  onChange: (color: string) => void;
}

export function CrayonRow({ value, onChange }: CrayonRowProps): JSX.Element {
  return (
    <div class="dock__row dock__row--scroll crayons" role="group" aria-label="Colours">
      {CRAYONS.map((c) => {
        const color = token(c.token);
        return (
          <button
            key={c.token}
            type="button"
            class="crayon"
            style={{ '--swatch': color }}
            aria-label={c.label}
            aria-pressed={value === color}
            onClick={() => {
              play('tap', 0.4);
              onChange(color);
            }}
          />
        );
      })}
    </div>
  );
}

export const SIZES = [8, 16, 30] as const;
export type SizeValue = (typeof SIZES)[number];

interface SizePickerProps {
  value: number;
  onChange: (size: SizeValue) => void;
}

export function SizePicker({ value, onChange }: SizePickerProps): JSX.Element {
  return (
    <div class="sizes" role="group" aria-label="Brush size">
      {SIZES.map((s, i) => (
        <button
          key={s}
          type="button"
          class="size"
          aria-label={['Small', 'Medium', 'Big'][i]}
          aria-pressed={value === s}
          onClick={() => {
            play('tap', 0.4);
            onChange(s);
          }}
        >
          <i style={{ '--dot': `${0.45 + i * 0.35}rem` }} />
        </button>
      ))}
    </div>
  );
}

interface StepperProps {
  value: number;
  min: number;
  max: number;
  step?: number;
  label: string;
  onChange: (value: number) => void;
  format?: (value: number) => string;
  /**
   * Makes the number itself tappable, for typing one in. Nudging up one at a
   * time is the right way to explore 3, 4, 5 — and a terrible way to reach 137.
   */
  onPick?: () => void;
}

export function Stepper(props: StepperProps): JSX.Element {
  const step = props.step ?? 1;
  const set = (next: number) => {
    const clamped = Math.min(props.max, Math.max(props.min, next));
    if (clamped !== props.value) {
      play('tap', 0.5);
      props.onChange(clamped);
    }
  };
  return (
    <div class="stepper" role="group" aria-label={props.label}>
      <ToyButton
        small
        round
        ghost
        label={`Fewer ${props.label}`}
        hideLabel
        disabled={props.value <= props.min}
        onClick={() => set(props.value - step)}
      >
        <Minus size={22} strokeWidth={3} />
      </ToyButton>
      {props.onPick ? (
        <button
          type="button"
          class="stepper__value stepper__value--pick"
          aria-label={`Type how many ${props.label}`}
          onClick={() => {
            clickSound();
            props.onPick?.();
          }}
        >
          {props.format ? props.format(props.value) : props.value}
        </button>
      ) : (
        <span class="stepper__value">{props.format ? props.format(props.value) : props.value}</span>
      )}
      <ToyButton
        small
        round
        ghost
        label={`More ${props.label}`}
        hideLabel
        disabled={props.value >= props.max}
        onClick={() => set(props.value + step)}
      >
        <Plus size={22} strokeWidth={3} />
      </ToyButton>
    </div>
  );
}

interface SheetProps {
  title: string;
  confirmLabel: string;
  accent?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/** In-app confirm. Never window.confirm — it freezes the canvas and looks alien. */
export function ConfirmSheet(props: SheetProps): JSX.Element {
  return (
    <div class="sheet-scrim" onClick={props.onCancel} role="presentation">
      <div class="sheet" role="dialog" aria-modal="true" aria-label={props.title} onClick={(e) => e.stopPropagation()}>
        <h2 class="sheet__title">{props.title}</h2>
        <div class="sheet__actions">
          <ToyButton label="Keep it" onClick={props.onCancel} />
          <ToyButton label={props.confirmLabel} accent={props.accent ?? token('--crayon-red')} onClick={props.onConfirm} />
        </div>
      </div>
    </div>
  );
}

interface ToastProps {
  message: string;
  actionLabel?: string;
  onAction?: () => void;
  onDone: () => void;
  /** How long before it takes itself away. */
  life?: number;
}

/**
 * What just happened, and the way to take it back. Putting a picture away is
 * reversible, and the moment a child most wants to reverse it is the second
 * after it vanishes — so the undo lives here rather than three taps away.
 */
export function Toast({ message, actionLabel, onAction, onDone, life = 6000 }: ToastProps): JSX.Element {
  useEffect(() => {
    const timer = setTimeout(onDone, life);
    return () => clearTimeout(timer);
  }, [message, life, onDone]);

  return (
    <div class="toast" role="status">
      <span class="toast__text">{message}</span>
      {actionLabel && onAction && (
        <button
          type="button"
          class="toast__action"
          onClick={() => {
            clickSound();
            onAction();
          }}
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}

/**
 * A single short instruction. It fades itself out so it never sits on top of
 * the toys a child is playing with; changing the text brings it back.
 */
export function Coach({ children, hideAfter = 7000 }: { children: ComponentChildren; hideAfter?: number }): JSX.Element | null {
  const [visible, setVisible] = useState(true);
  const key = String(children);

  useEffect(() => {
    setVisible(true);
    const timer = setTimeout(() => setVisible(false), hideAfter);
    return () => clearTimeout(timer);
  }, [key, hideAfter]);

  return visible ? <p class="coach">{children}</p> : null;
}
